import React, { useEffect, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import jwtDecode from 'jwt-decode'

import { initStateObject, objectReducer } from 'reducers/default'
import authApi from 'api/auth'
import CampaignSetupProvider, {
  useCampaignSetup,
  useCurrentStep,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import CampaignStepWrapper from 'components/Recommendations/EventCampaigns/CampaignStepWrapper'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import ErrorMessage from 'components/Shared/ErrorMessage'
import CampaignConfigProvider from 'components/Recommendations/Campaign/RunningCampaign/CampaignConfig/CampaignConfigProvider'
import { CampaignMenuContextProvider } from 'components/Recommendations/Campaign/CampaignMenu/CampaignMenuContext'
import { ExpandedLogoWhiteIcon, EyeIcon, EditIcon } from 'images'
import EventHeader from 'components/Event/Details/EventHeader'

const getAnonState = (anonScopes) => {
  const hasWrite = anonScopes.includes('setup_process:write')
  const hasBoost = anonScopes.includes('setup_process:boost')

  if (hasWrite) {
    return {
      labelKey: 'AnonSetup.state.editor',
      Icon: EditIcon,
      iconClassName: 'anon-setup-page_topbar_state_edit',
    }
  }
  if (hasBoost) {
    return {
      labelKey: 'AnonSetup.state.readAndPublish',
      Icon: EyeIcon,
      iconClassName: 'anon-setup-page_topbar_state_eye',
    }
  }
  return {
    labelKey: 'AnonSetup.state.readOnly',
    Icon: EyeIcon,
    iconClassName: 'anon-setup-page_topbar_state_eye',
  }
}

const AnonSetupBody = ({ eventId, eventState, dispatchEventAction }) => {
  const { t } = useTranslation()
  const { setupNotFound } = useCampaignSetup()
  const { currentStep } = useCurrentStep()
  const isPublishedScreen = currentStep === 5

  return (
    <>
      {!isPublishedScreen && (
        <EventHeader
          classes=""
          event={eventState.content}
          crEvent={null}
          isLoading={eventState.loading}
          showActiveBadge={false}
          renderMenu={() => null}
        />
      )}

      {setupNotFound ? (
        <div className="anon-setup-message">{t('AnonSetup.setupNotFound')}</div>
      ) : (
        <CampaignStepWrapper
          eventId={String(eventId)}
          eventState={[eventState, dispatchEventAction]}
          fetchedCampaigns={null}
          resetKey={0}
        />
      )}
    </>
  )
}

AnonSetupBody.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventState: PropTypes.object.isRequired,
  dispatchEventAction: PropTypes.func.isRequired,
}

export function AnonSetupPage({ match }) {
  const { eventId } = match.params
  const { t } = useTranslation()
  const [accessToken, setAccessToken] = useState(null)
  const [anonScopes, setAnonScopes] = useState([])
  const [tokenError, setTokenError] = useState(null)
  const [eventState, dispatchEventAction] = useReducer(objectReducer, initStateObject)

  // Exchange code for token on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const code = searchParams.get('code')

    if (!code) {
      setTokenError(t('common.genericError'))
      return
    }

    authApi
      .getAnonymousToken(code)
      .then((response) => {
        if (response.error) {
          setTokenError(t('common.genericError'))
          return
        }
        const token = response.data.access_token
        sessionStorage.setItem('anonymous-session', JSON.stringify({ access_token: token }))
        const decoded = jwtDecode(token)
        setAnonScopes(decoded.scopes || [])
        setAccessToken(token)
      })
      .catch(() => setTokenError(t('common.genericError')))
  }, [t])

  const anonState = accessToken ? getAnonState(anonScopes) : null
  const StateIcon = anonState?.Icon

  return (
    <div className="anon-setup-page">
      <div className="anon-setup-page_topbar">
        <ExpandedLogoWhiteIcon className="anon-setup-page_topbar_logo" alt="Future Demand" />
        {anonState && (
          <div className="anon-setup-page_topbar_state">
            <StateIcon className={anonState.iconClassName} width={16} height={16} />
            <span>{t(anonState.labelKey)}</span>
          </div>
        )}
      </div>

      <div className="anon-setup-page_content">
        {tokenError && (
          <div className="container mt-5">
            <ErrorMessage danger>{tokenError}</ErrorMessage>
          </div>
        )}

        {!tokenError && !accessToken && (
          <div className="d-flex justify-content-center mt-5">
            <LoadingSpinner />
          </div>
        )}

        {!tokenError && accessToken && (
          <div className="container-fluid py-4" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <CampaignSetupProvider eventId={String(eventId)} anonScopes={anonScopes}>
              <CampaignConfigProvider event={eventState?.content}>
                <CampaignMenuContextProvider event={eventState.content}>
                  <AnonSetupBody
                    eventId={eventId}
                    eventState={eventState}
                    dispatchEventAction={dispatchEventAction}
                  />
                </CampaignMenuContextProvider>
              </CampaignConfigProvider>
            </CampaignSetupProvider>
          </div>
        )}
      </div>

      <div className="anon-setup-page_bottombar">{t('AnonSetup.poweredBy')}</div>
    </div>
  )
}

AnonSetupPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
}
